import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';

import { Slider } from '@atlas/components';
import {
  constants,
  CryptoFormatter,
  CurrencyFormatter,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useCasinoLastWinners,
  useSettings,
} from '@atlas/core';
import { casinoRoutes } from '@atlas/routing';
import { CasinoLastWinnersTile, ChipsItem, Icon, Label, Loader } from '@atlas/ui';

import { callUserSelfExcludedPopup, normalizePath } from 'components/Casino/utils';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useIsRtl } from 'hooks/useIsRtl';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN, LocalConfig } from 'src/common/utils';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import gamePlaceholder from 'static/assets/svg/empty-casino.svg';
import avatarPlaceholder from 'static/assets/svg/icon-user.svg';

const { game: casinoGamePath } = casinoRoutes;
const { CRYPTO_CURRENCY_CODE } = constants;

const userName = login => {
  if (login.length > 3) return `${login.slice(0, 3)}*`;

  return `${login.slice(0, -2)}***`;
};

const formatWinnerItem = (winner, languageCode) => {
  const info = winner.info || {};
  const { iconUrl, id: gameId, name: gameName } = winner.game || {};

  const currency = info.currency ?? '';

  const formatter = CRYPTO_CURRENCY_CODE.includes(currency)
    ? new CryptoFormatter(languageCode, currency)
    : new CurrencyFormatter(languageCode, currency);

  const formattedAmount = formatter.format(info.amount ?? 0);

  const gameImage = iconUrl ? imageUrlCDN(iconUrl, CDN_TYPE_GAME) : gamePlaceholder;

  return {
    amount: formattedAmount,
    avatarUrl: avatarPlaceholder,
    currency: '',
    id: gameId,
    imageUrlCDN: gameImage,
    login: userName(info.login ?? ''),
    name: gameName ?? '',
    uniqId: info.transactionId,
  };
};

const convertToMilliseconds = seconds => seconds * 1000;
const DEFAULT_LAST_WINNER_SPEED = 2;

const LobbyLastWinnersBlock = ({ blockTitleKey, imageUrl }) => {
  const [swiper, setSwiper] = useState(null);
  const prevSlide = () => swiper && swiper.slidePrev();
  const nextSlide = () => swiper && swiper.slideNext();
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = () => toggleVisibilityImg(false);

  const projectId = LocalConfig.get('projectId');
  const { lastWinners = [], loading } = useCasinoLastWinners(projectId);

  const { lang } = useSettings();
  const applicationState = useApplicationState();
  const isAccountBlocked = useAccountBlocked();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const {
    account,
    account: { selfExcludedDueDate } = {},
    authenticated,
    bankAccounts,
  } = applicationState;

  const history = useHistory();
  const {
    location: { pathname },
    push: historyPush,
  } = history;

  const showAccountBlockedPopup = useCallback(() => {
    NotificationManager.display({
      id: 'accountBlocked',
      type: 'accountBlocked',
    });
  }, []);

  const path = normalizePath(pathname);

  const lastWinnersAutoplaySpeed = SiteConfigManager.getConfig(
    'config.config.lastWinners.desktop.update_time',
  );
  const sliderSpeed = useMemo(
    () =>
      lastWinnersAutoplaySpeed
        ? convertToMilliseconds(lastWinnersAutoplaySpeed)
        : convertToMilliseconds(DEFAULT_LAST_WINNER_SPEED),
    [lastWinnersAutoplaySpeed],
  );

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const onPlayClick = useCallback(
    async gameId => {
      if (isAccountBlocked) {
        showAccountBlockedPopup();
        return;
      }

      if (selfExcludedDueDate && dayjs().isBefore(selfExcludedDueDate)) {
        return callUserSelfExcludedPopup();
      }
      const gameUrl = new URL(`${path}/${casinoGamePath}/${gameId}`, location.origin);

      gameUrl.searchParams.append('lastWinner', 'true');
      gameUrl.searchParams.set('from', location.pathname + location.search);

      if (
        authenticated &&
        needsVerification(account, bankAccounts) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
      ) {
        return NotificationManager.display({
          data: { history },
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (!canProceedSpentLimit) return;

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (!canProceedSelfRegulation) return;

      return historyPush(gameUrl.pathname + gameUrl.search);
    },
    [
      isAccountBlocked,
      selfExcludedDueDate,
      path,
      authenticated,
      account,
      bankAccounts,
      showSpentLimitPopupIfNeeded,
      showSelfRegulationPopupIfNeeded,
      historyPush,
      showAccountBlockedPopup,
      history,
    ],
  );

  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);

  const finalItems = useMemo(() => {
    return (lastWinners || []).map(winner => formatWinnerItem(winner, languageCode));
  }, [lastWinners, languageCode]);

  if (loading) {
    return (
      <div className="casino-lobby__item lobby-last-winners">
        <Loader className="lobby-last-winners__loader" />
      </div>
    );
  }

  if (!lastWinners || lastWinners.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-last-winners">
      <div className="casino-lobby__header">
        <div className="casino-lobby__right">
          <ChipsItem
            className="casino-lobby__arrow casino-lobby__arrow-left"
            label={<Icon name="arrow-left" />}
            onClick={prevSlide}
          />
          <ChipsItem
            className="casino-lobby__arrow casino-lobby__arrow-right"
            label={<Icon name="arrow-right" />}
            onClick={nextSlide}
          />
        </div>
      </div>
      <div className="casino-lobby__content lobby-last-winners__wr">
        <div className="lobby-last-winners__left">
          <div className="lobby-last-winners__promo">
            {isShowImg && (
              <div className="lobby-last-winners__icon">
                <img
                  alt={blockTitleKey}
                  onError={handleError}
                  role="presentation"
                  src={
                    imageUrl
                      ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                      : `${BASE_PATH || ''}/static/assets/images/last-winner.svg`
                  }
                  width={66}
                />
              </div>
            )}
            {blockTitleKey && (
              <div className="lobby-last-winners__title">
                <Label message={blockTitleKey} />
              </div>
            )}
          </div>
        </div>
        <div className="lobby-last-winners__slider">
          <Slider
            autoplay={{
              delay: convertToMilliseconds(DEFAULT_LAST_WINNER_SPEED),
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            dir={dir}
            direction="horizontal"
            loop={false}
            onBeforeInit={setSwiper}
            spaceBetween={8}
            speed={sliderSpeed}
          >
            {finalItems.map((item, index) => (
              <Slider.Slide key={`${item.uniqId || 'winner'}-${index}`}>
                <CasinoLastWinnersTile
                  index={index}
                  isLoading={false}
                  isMobile={false}
                  item={item}
                  onPlayClick={onPlayClick}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default LobbyLastWinnersBlock;
